/**
 * Reports, defined as metadata like everything else.
 *
 * The definition drives the whole screen: which filters appear, which are
 * mandatory (a report does not run until they are answered — docs/10), the
 * columns, and which of them are totalled in the pinned footer.
 *
 * The QUERY is not metadata. It lives in code on the server, because a report
 * that carries its own SQL is an injection surface and a schema-change trap.
 */
export type ReportGroup =
  | 'Management' | 'Project' | 'Operation' | 'Supply chain' | 'Inventory' | 'Tools and Equipment'
  | 'Integration'

export interface ReportFilterDef {
  key: string
  label: string
  control: 'lookup' | 'dateRange' | 'select' | 'text' | 'checkbox'
  required?: boolean
  lookup?: { entity: string }
  options?: ReadonlyArray<{ value: string; label: string }>
  hint?: string
}

export interface ReportColumnDef {
  key: string
  label: string
  type?: 'text' | 'docno' | 'date' | 'money' | 'qty' | 'rate' | 'pct' | 'status' | 'duration'
  width?: number
  align?: 'left' | 'right'
  /** Included in the pinned footer total. */
  total?: boolean
  /** Colour-coded against zero: green favourable, red not. */
  signed?: boolean
}

/**
 * What a row on this report drills into (docs/10 — the `DETAILS` modal).
 *
 * A total on a report is only trustworthy if you can open it and see the
 * documents underneath. `by` names the column on the row that identifies the
 * thing to drill on, and `docTypes` bounds what is listed — a cost row drills
 * into invoices, not into the material requests behind them.
 */
export interface ReportDrillDef {
  /** The row column holding the id: `id`, `projectId`, `itemId`, `supplierId`. */
  readonly by: 'project' | 'item' | 'supplier' | 'document'
  readonly key: string
  readonly docTypes?: readonly string[]
  /** What the modal is called. */
  readonly label?: string
}

export interface ReportDef {
  key: string
  route: string
  group: ReportGroup
  label: string
  blurb: string
  filters: readonly ReportFilterDef[]
  columns: readonly ReportColumnDef[]
  /** Most financial reports offer both; the query decides what changes. */
  hasSummaryDetail?: boolean
  /** Shown above the table when the numbers need a caveat. */
  caveat?: string
  /** Clicking a row opens the documents behind it. */
  drill?: ReportDrillDef
}

export const defineReport = <const T extends ReportDef>(def: T): T => def

export const rf = {
  project: (required = false): ReportFilterDef =>
    ({ key: 'projectId', label: 'Project', control: 'lookup', required, lookup: { entity: 'project' } }),
  period: (label = 'Period', required = true): ReportFilterDef =>
    ({ key: 'period', label, control: 'dateRange', required }),
  supplier: (): ReportFilterDef =>
    ({ key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } }),
  customer: (): ReportFilterDef =>
    ({ key: 'customerId', label: 'Customer', control: 'lookup', lookup: { entity: 'customer' } }),
  warehouse: (required = false): ReportFilterDef =>
    ({ key: 'warehouseId', label: 'Warehouse', control: 'lookup', required, lookup: { entity: 'warehouse' } }),
  jobType: (): ReportFilterDef =>
    ({ key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } }),
  employee: (): ReportFilterDef =>
    ({ key: 'employeeId', label: 'Employee', control: 'lookup', lookup: { entity: 'employee' } }),
  category: (): ReportFilterDef =>
    ({ key: 'categoryId', label: 'Item category', control: 'lookup', lookup: { entity: 'item_category' } }),
  status: (): ReportFilterDef =>
    ({ key: 'documentStatus', label: 'Document status', control: 'select', options: [
      { value: 'DRAFT', label: 'Draft' }, { value: 'IN_REVIEW', label: 'In review' },
      { value: 'APPROVED', label: 'Approved' }, { value: 'CANCELLED', label: 'Cancelled' },
    ] }),
  includeSub: (): ReportFilterDef =>
    ({ key: 'includeSubProjects', label: 'Include sub-projects', control: 'checkbox' }),
  /**
   * How a stock report is grouped (docs/10 §Inventory).
   *
   * The same stock read three ways: by where it is held, by the job it is held
   * for, or by the material itself — which is the one that answers "how much of
   * this do we have anywhere".
   */
  mode: (options: ReadonlyArray<[string, string]>, required = false): ReportFilterDef => ({
    key: 'mode', label: 'Show by', control: 'select', required,
    options: options.map(([value, label]) => ({ value, label })),
  }),
}

export const rc = {
  doc: (key = 'documentNo', label = 'Document no'): ReportColumnDef => ({ key, label, type: 'docno', width: 158 }),
  date: (key: string, label: string): ReportColumnDef => ({ key, label, type: 'date', width: 130 }),
  text: (key: string, label: string, width?: number): ReportColumnDef => ({ key, label, type: 'text', ...(width ? { width } : {}) }),
  money: (key: string, label: string, total = true): ReportColumnDef =>
    ({ key, label, type: 'money', width: 150, align: 'right', total }),
  signed: (key: string, label: string): ReportColumnDef =>
    ({ key, label, type: 'money', width: 150, align: 'right', total: true, signed: true }),
  qty: (key: string, label: string, total = false): ReportColumnDef =>
    ({ key, label, type: 'qty', width: 118, align: 'right', total }),
  pct: (key: string, label: string): ReportColumnDef => ({ key, label, type: 'pct', width: 110, align: 'right' }),
  status: (key = 'documentStatus', label = 'Status'): ReportColumnDef => ({ key, label, type: 'status', width: 136 }),
}
